import { useEffect, useMemo, useState } from 'react'
import {
  BanknoteIcon,
  Calculator,
  Download,
  Eye,
  Pencil,
  Plus,
  Save,
  TrendingDown,
  Trash2,
  X,
} from 'lucide-react'
import { useDirty } from '@/hooks/useDirty'
import { downloadSchedulePdf, useReportBrand } from '@/lib/pdf'
import { PostingBanner } from '@/components/PostingBanner'
import { PostingSelectorModal, type PostingSelectorItem } from '@/components/PostingSelectorModal'
import { useUnpostedPreview } from '@/features/reporting/queries'
import { formatDate } from '@/lib/locale'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DataTable } from '@/components/DataTable'
import { Combobox, type ComboboxOption } from '@/components/Combobox'
import { StatCard } from '@/components/StatCard'
import { Toolbar } from '@/components/Toolbar'
import { Modal } from '@/components/Modal'
import { FormField } from '@/components/FormField'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { formatCurrencyPrecise } from '@/lib/locale'
import { useCoa } from '@/features/coa/queries'
import { usePostingStatus, usePostSelected } from '@/features/accruals/queries'
import {
  useCreateSchedule,
  useDeleteSchedule,
  useScheduleDetail,
  useSchedules,
  useUpdateSchedule,
} from '@/features/fixedAssets/queries'
import { EditScheduleDialog } from '@/components/EditScheduleDialog'
import { buildDepreciationSchedule } from '@/features/fixedAssets/calc'
import {
  ASSET_CATEGORIES,
  METHOD_LABEL,
  type CreateSchedulePayload,
  type DepreciationMethod,
  type Schedule,
  type ScheduleEntry,
} from '@/features/fixedAssets/types'
import type { TaxonomyItem } from '@/features/coa/types'

function errorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

function fmtMoney(v: number | string | null | undefined): string {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
  if (!isFinite(n)) return formatCurrencyPrecise(0)
  return formatCurrencyPrecise(n)
}

function taxOption(t: TaxonomyItem): ComboboxOption<string> {
  return {
    value: t.account_code,
    label: `${t.account_code} - ${t.subcategory_name}`,
    description: t.main_category,
  }
}

const CATEGORY_OPTIONS: ComboboxOption<string>[] = ASSET_CATEGORIES.map((c) => ({
  value: c,
  label: c,
}))

const METHOD_OPTIONS: ComboboxOption<DepreciationMethod>[] = (
  Object.keys(METHOD_LABEL) as DepreciationMethod[]
).map((m) => ({ value: m, label: METHOD_LABEL[m] }))

type FormState = {
  description: string
  category: string
  purchaseDate: string
  cost: string
  method: DepreciationMethod
  usefulLifeYears: string
  durationMonths: string
  residualPercent: string
  startDate: string
  debitAccount: string
  creditAccount: string
  thirdAccount: string
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function defaultForm(): FormState {
  return {
    description: '',
    category: '',
    purchaseDate: todayIso(),
    cost: '',
    method: 'SLN',
    usefulLifeYears: '',
    durationMonths: '',
    residualPercent: '',
    startDate: todayIso(),
    debitAccount: '',
    creditAccount: '',
    thirdAccount: '',
  }
}

export function FixedAssetsPage() {
  const { toast } = useToast()
  const coa = useCoa()
  const schedules = useSchedules()
  const posting = usePostingStatus()
  const createMut = useCreateSchedule()
  const updateMut = useUpdateSchedule()
  const deleteMut = useDeleteSchedule()
  // postMut (legacy bulk post) is replaced by postSelectedMut so a click
  // on Post Now no longer drags in unrelated sources.
  const postSelectedMut = usePostSelected()
  // Why: one modal, two modes. View pending opens it read-only (no
  // checkboxes, no Post button); Post Now opens it editable. Either
  // way the modal only shows depreciation rows - never accruals, bills,
  // or other sources.
  const [postSelectorOpen, setPostSelectorOpen] = useState(false)
  const [selectorReadOnly, setSelectorReadOnly] = useState(false)
  const unpostedPreview = useUnpostedPreview(postSelectorOpen || false)

  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState<FormState>(() => defaultForm())
  const [preview, setPreview] = useState<ScheduleEntry[]>([])
  const [formErr, setFormErr] = useState<string | null>(null)
  const [viewId, setViewId] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Schedule | null>(null)
  const [editTarget, setEditTarget] = useState<Schedule | null>(null)

  const isAccountOptions = useMemo<ComboboxOption<string>[]>(() => {
    return (coa.data ?? [])
      .filter((t) => t.statement_type === 'Income Statement')
      .map(taxOption)
  }, [coa.data])

  const bsAccountOptions = useMemo<ComboboxOption<string>[]>(() => {
    return (coa.data ?? [])
      .filter((t) => t.statement_type === 'Balance Sheet')
      .map(taxOption)
  }, [coa.data])

  const rows = useMemo(() => schedules.data ?? [], [schedules.data])

  const stats = useMemo(() => {
    const totalCost = rows.reduce(
      (sum, s) => sum + (parseFloat(String(s.initial_amount ?? 0)) || 0),
      0,
    )
    return { count: rows.length, totalCost }
  }, [rows])

  // Why: posting.data.unpostedSchedules is the sum across depreciation +
  // prepaid + loan schedules. Read only the depreciation bucket so the
  // banner on this page matches its own scope (a pending prepaid /
  // loan entry would otherwise show up here as "depreciation").
  const unpostedNum = posting.data?.unpostedSchedulesByType?.depreciation ?? 0

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  // Dirty-check the create-asset modal. Mirrors the prepaid pattern -
  // empty defaultForm() baseline so any field change unlocks Save.
  // Edits use a separate modal that owns its own dirty tracking.
  const formBaseline = useMemo(() => defaultForm(), [])
  const dirty = useDirty(form, formBaseline)

  const handleCalculate = () => {
    setFormErr(null)
    const cost = parseFloat(form.cost)
    const residual = parseFloat(form.residualPercent)
    const life = parseInt(form.usefulLifeYears, 10)
    const duration = parseInt(form.durationMonths, 10)
    if (!(cost > 0) || isNaN(residual) || !(life > 0) || !(duration > 0) || !form.startDate) {
      setFormErr('Fill in cost, residual %, useful life, duration, and start date.')
      setPreview([])
      return
    }
    const entries = buildDepreciationSchedule({
      cost,
      residualPercent: residual,
      usefulLifeYears: life,
      durationMonths: duration,
      method: form.method,
      startDate: form.startDate,
    })
    setPreview(entries)
  }

  const handleSave = async () => {
    if (preview.length === 0) {
      setFormErr('Calculate the schedule first.')
      return
    }
    if (!form.debitAccount) {
      setFormErr('Depreciation expense (I/S) account is required - the debit side.')
      return
    }
    if (!form.creditAccount) {
      setFormErr('Accumulated depreciation (B/S) account is required - the credit side.')
      return
    }
    const payload: CreateSchedulePayload = {
      schedule_type: 'depreciation',
      description: form.description,
      category: form.category,
      initial_amount: parseFloat(form.cost),
      start_date: form.startDate,
      purchase_date: form.purchaseDate || null,
      duration_periods: parseInt(form.durationMonths, 10),
      params: {
        method: form.method,
        useful_life_years: parseInt(form.usefulLifeYears, 10),
        residual_value_percent: parseFloat(form.residualPercent),
      },
      debit_account: form.debitAccount,
      credit_account: form.creditAccount,
      third_account: form.thirdAccount || null,
      entries: preview,
    }
    try {
      await createMut.mutateAsync(payload)
      toast({ title: 'Depreciation schedule saved.' })
      setFormOpen(false)
      setForm(defaultForm())
      setPreview([])
    } catch (err) {
      setFormErr(errorMessage(err))
    }
  }

  const runDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMut.mutateAsync(deleteTarget.id)
      toast({ title: 'Schedule voided.' })
      setDeleteTarget(null)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Void failed',
        description: errorMessage(err),
      })
    }
  }

  const depreciationItems = useMemo<PostingSelectorItem[]>(() => {
    const rows = unpostedPreview.data?.schedules ?? []
    return rows
      .filter((r) => r.scheduleType === 'depreciation')
      .map((r) => ({
        id: r.id,
        cells: [
          formatDate(r.entryDate),
          <span className="block max-w-[320px] truncate" title={r.scheduleDescription ?? undefined}>
            {r.scheduleDescription || r.category || 'Schedule'}
          </span>,
          `Period ${r.periodNumber}`,
          <span className="text-xs text-muted-foreground">
            Dr {r.debitAccount || '-'} / Cr {r.creditAccount || '-'}
          </span>,
          fmtMoney(r.amount),
        ],
        blocked: r.blocked,
        blockedReason: r.blocked ? 'Reporting period is closed' : undefined,
      }))
  }, [unpostedPreview.data])

  const depreciationColumns = [
    { header: 'Date', width: 'w-32' },
    { header: 'Description' },
    { header: 'Period', width: 'w-24' },
    { header: 'Accounts' },
    { header: 'Amount', align: 'right' as const, width: 'w-28' },
  ]

  const runPost = async (selection: Record<string, number[]>) => {
    const ids = selection.depreciation ?? []
    if (ids.length === 0) return
    try {
      const r = await postSelectedMut.mutateAsync({ source: 'depreciation', ids })
      toast({
        title: 'Posted to GL',
        description: `Posted ${r.posted ?? ids.length} depreciation entr${(r.posted ?? ids.length) === 1 ? 'y' : 'ies'}.`,
      })
      setPostSelectorOpen(false)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Posting failed',
        description: errorMessage(err),
      })
    }
  }

  if (schedules.isLoading || coa.isLoading) {
    return (
      <Page title="Fixed assets">
        <Card>
          <CardContent className="py-10">
            <LoadingSpinner />
          </CardContent>
        </Card>
      </Page>
    )
  }

  if (schedules.isError) {
    return (
      <Page title="Fixed assets">
        <Alert variant="destructive">
          <AlertDescription>Could not load schedules: {errorMessage(schedules.error)}</AlertDescription>
        </Alert>
      </Page>
    )
  }

  return (
    <Page
      title="Fixed assets"
      description="Create depreciation schedules, preview the month-by-month entries, then post them to the GL."
      width="wide"
      actions={
        <Button onClick={() => setFormOpen((v) => !v)}>
          {formOpen ? <X className="size-4" /> : <Plus className="size-4" />}
          {formOpen ? 'Close form' : 'New schedule'}
        </Button>
      }
    >
      <PostingBanner
        count={unpostedNum}
        label="depreciation entries"
        singularLabel="depreciation entry"
        source="depreciation"
        onView={() => { setSelectorReadOnly(true); setPostSelectorOpen(true) }}
        onPostNow={() => { setSelectorReadOnly(false); setPostSelectorOpen(true) }}
        busy={postSelectedMut.isPending}
      />

      <PostingSelectorModal
        open={postSelectorOpen}
        onOpenChange={setPostSelectorOpen}
        title={selectorReadOnly ? 'Pending depreciation entries' : 'Post depreciation entries'}
        description={selectorReadOnly
          ? 'These depreciation entries are queued to post. Click Post Now to choose which to post.'
          : 'Tick the entries you want to post. Posting only affects the rows you select - bank statements, accruals, and journal entries are untouched.'}
        loading={unpostedPreview.isLoading}
        posting={postSelectedMut.isPending}
        readOnly={selectorReadOnly}
        onSwitchToPost={() => setSelectorReadOnly(false)}
        tabs={[
          {
            key: 'depreciation',
            label: 'Depreciation',
            items: depreciationItems,
            columns: depreciationColumns,
            emptyMessage: 'No depreciation entries are pending.',
          },
        ]}
        onPost={runPost}
      />

      {/* Why: removed the "Unposted" stat - the Post Now banner above
          already shows the unposted count + view + post action. Same
          treatment applied to Accruals, Prepaid, and Loan pages so the
          stats rows stay consistent. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <StatCard label="Active schedules" value={stats.count} icon={BanknoteIcon} />
        <StatCard label="Total asset value" value={fmtMoney(stats.totalCost)} icon={TrendingDown} />
      </div>

      {formOpen ? (
        <Card>
          <CardContent className="space-y-6 pt-6">
            <FormSection title="Asset information">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                <FormField
                  label="Description"
                  placeholder="e.g. Office equipment"
                  value={form.description}
                  onChange={(e) => update('description', e.currentTarget.value)}
                />
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Category</label>
                  <Combobox
                    options={CATEGORY_OPTIONS}
                    value={form.category || null}
                    placeholder="Select category…"
                    onChange={(v) => update('category', v ?? '')}
                  />
                </div>
                <FormField
                  label="Purchase date"
                  type="date"
                  value={form.purchaseDate}
                  onChange={(e) => update('purchaseDate', e.currentTarget.value)}
                />
                <FormField
                  label="Initial cost ($)"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="0.00"
                  value={form.cost}
                  onChange={(e) => update('cost', e.currentTarget.value)}
                />
              </div>
            </FormSection>

            <FormSection title="Depreciation parameters">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Method</label>
                  <Combobox
                    options={METHOD_OPTIONS}
                    value={form.method}
                    placeholder="Select method…"
                    onChange={(v) => update('method', (v as DepreciationMethod) ?? 'SLN')}
                  />
                </div>
                <FormField
                  label="Useful life (years)"
                  type="number"
                  min="1"
                  placeholder="e.g. 5"
                  value={form.usefulLifeYears}
                  onChange={(e) => update('usefulLifeYears', e.currentTarget.value)}
                />
                <FormField
                  label="Duration (periods / months)"
                  type="number"
                  min="1"
                  placeholder="e.g. 60"
                  value={form.durationMonths}
                  onChange={(e) => update('durationMonths', e.currentTarget.value)}
                />
                <FormField
                  label="Residual value (% of cost)"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="e.g. 10"
                  value={form.residualPercent}
                  onChange={(e) => update('residualPercent', e.currentTarget.value)}
                />
                <FormField
                  label="Depreciation start date"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => update('startDate', e.currentTarget.value)}
                />
              </div>
            </FormSection>

            <FormSection title="GL accounts">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    Depreciation expense (I/S) <span className="text-destructive">*</span>
                  </label>
                  <Combobox
                    options={isAccountOptions}
                    value={form.debitAccount || null}
                    placeholder="Search expense account…"
                    onChange={(v) => update('debitAccount', v ?? '')}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    Accumulated depreciation (B/S) <span className="text-destructive">*</span>
                  </label>
                  <Combobox
                    options={bsAccountOptions}
                    value={form.creditAccount || null}
                    placeholder="Search accumulated account…"
                    onChange={(v) => update('creditAccount', v ?? '')}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Fixed asset account (B/S)</label>
                  <Combobox
                    options={bsAccountOptions}
                    value={form.thirdAccount || null}
                    placeholder="Search fixed asset account…"
                    onChange={(v) => update('thirdAccount', v ?? '')}
                  />
                </div>
              </div>
            </FormSection>

            {formErr ? (
              <Alert variant="destructive">
                <AlertDescription>{formErr}</AlertDescription>
              </Alert>
            ) : null}

            <Toolbar align="end">
              <Button variant="secondary" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button variant="outline" onClick={handleCalculate}>
                <Calculator className="size-4" /> Calculate preview
              </Button>
              {preview.length > 0 ? (
                <Button onClick={handleSave} disabled={!dirty || createMut.isPending}>
                  <Save className="size-4" /> Save schedule
                </Button>
              ) : null}
            </Toolbar>

            {preview.length > 0 ? (
              <>
                <h3 className="mb-2 text-sm font-semibold">Depreciation preview</h3>
                <DataTable
                  sortable={false}
                  rows={preview}
                  columns={[
                    {
                      header: () => <span className="block text-center">#</span>,
                      accessorKey: 'period_number',
                      cell: ({ row }) => (
                        <span className="block text-center text-xs text-muted-foreground">
                          {row.original.period_number}
                        </span>
                      ),
                    },
                    {
                      header: 'Period',
                      accessorKey: 'entry_date',
                      cell: ({ row }) => (
                        <span className="whitespace-nowrap">
                          {new Date(row.original.entry_date).toLocaleDateString()}
                        </span>
                      ),
                    },
                    {
                      header: () => <span className="block text-right">Monthly</span>,
                      accessorKey: 'amount',
                      cell: ({ row }) => (
                        <span className="block text-right font-mono text-xs">
                          {fmtMoney(row.original.amount)}
                        </span>
                      ),
                    },
                    {
                      header: () => <span className="block text-right">Accumulated</span>,
                      accessorKey: 'accumulated',
                      cell: ({ row }) => (
                        <span className="block text-right font-mono text-xs">
                          {fmtMoney(row.original.accumulated)}
                        </span>
                      ),
                    },
                    {
                      header: () => <span className="block text-right">Net book value</span>,
                      accessorKey: 'balance',
                      cell: ({ row }) => (
                        <span className="block text-right font-mono text-xs">
                          {fmtMoney(row.original.balance)}
                        </span>
                      ),
                    },
                  ]}
                />
              </>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="space-y-4 pt-6">
          <h2 className="text-base font-semibold">Saved schedules</h2>
          {rows.length === 0 ? (
            <EmptyState
              icon={BanknoteIcon}
              title="No depreciation schedules yet"
              description="Click New schedule to depreciate your first asset."
              action={
                <Button onClick={() => setFormOpen(true)}>
                  <Plus className="size-4" /> Create schedule
                </Button>
              }
            />
          ) : (
            <>
              {/* md+: DataTable */}
              <div className="hidden md:block">
                <DataTable
                  sortable={false}
                  rows={rows}
                  columns={[
                    { header: 'Asset', accessorKey: 'description' },
                    { header: 'Category', accessorKey: 'category' },
                    {
                      header: () => <span className="block text-right">Cost</span>,
                      accessorKey: 'initial_amount',
                      cell: ({ row }) => (
                        <span className="block text-right font-mono text-xs">
                          {fmtMoney(row.original.initial_amount)}
                        </span>
                      ),
                    },
                    {
                      header: 'Method',
                      accessorKey: 'params',
                      cell: ({ row }) => row.original.params?.method ?? 'SLN',
                    },
                    {
                      header: 'Duration',
                      accessorKey: 'duration_periods',
                      cell: ({ row }) => `${row.original.duration_periods ?? 0} mo`,
                    },
                    {
                      header: 'Start',
                      accessorKey: 'start_date',
                      cell: ({ row }) =>
                        row.original.start_date
                          ? new Date(row.original.start_date).toLocaleDateString()
                          : '-',
                    },
                    {
                      header: 'Status',
                      accessorKey: 'status',
                      cell: ({ row }) => (
                        <Badge variant={row.original.status === 'active' ? 'success' : 'secondary'}>
                          {row.original.status}
                        </Badge>
                      ),
                    },
                    {
                      id: 'actions',
                      header: () => <span className="block text-right">Actions</span>,
                      cell: ({ row }) => (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setViewId(row.original.id)}
                            title="View details"
                          >
                            <Eye className="size-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditTarget(row.original)}
                            title="Edit details"
                          >
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteTarget(row.original)}
                            title="Void schedule"
                          >
                            <Trash2 className="size-4 text-destructive" />
                          </Button>
                        </div>
                      ),
                    },
                  ]}
                />
              </div>
              {/* xs/sm: cards */}
              <div className="space-y-3 md:hidden">
                {rows.map((s) => (
                  <ScheduleCard
                    key={s.id}
                    schedule={s}
                    onView={() => setViewId(s.id)}
                    onEdit={() => setEditTarget(s)}
                    onDelete={() => setDeleteTarget(s)}
                  />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ViewScheduleModal id={viewId} onClose={() => setViewId(null)} />

      <EditScheduleDialog
        open={editTarget !== null}
        onOpenChange={(v) => !v && setEditTarget(null)}
        schedule={editTarget ? {
          id: editTarget.id,
          description: editTarget.description,
          category: editTarget.category,
          debit_account: editTarget.debit_account,
          credit_account: editTarget.credit_account,
          third_account: editTarget.third_account,
        } : null}
        busy={updateMut.isPending}
        onSubmit={async (id, body) => {
          try {
            await updateMut.mutateAsync({ id, body: body as Partial<CreateSchedulePayload> })
            toast({ title: 'Schedule updated.' })
            setEditTarget(null)
          } catch (err) {
            toast({ variant: 'destructive', title: 'Update failed', description: errorMessage(err) })
          }
        }}
      />

      <Modal.Confirm
        open={deleteTarget !== null}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Void schedule?"
        body={
          <p>
            This will void this depreciation schedule and create reversing entries for any periods
            already posted to the general ledger. Continue?
          </p>
        }
        confirmLabel="Void schedule"
        onConfirm={runDelete}
        onCancel={() => setDeleteTarget(null)}
        busy={deleteMut.isPending}
      />

    </Page>
  )
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-3 border-b border-primary pb-1 text-sm font-semibold uppercase tracking-wide text-foreground">
        {title}
      </h3>
      {children}
    </section>
  )
}

function ScheduleCard({
  schedule,
  onView,
  onEdit,
  onDelete,
}: {
  schedule: Schedule
  onView: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div className="space-y-2 rounded-md border bg-card p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{schedule.description || 'Untitled'}</p>
          <p className="truncate text-xs text-muted-foreground">
            {schedule.category || '-'} · {schedule.params?.method ?? 'SLN'}
          </p>
        </div>
        <Badge variant={schedule.status === 'active' ? 'success' : 'secondary'}>
          {schedule.status}
        </Badge>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <span>Cost: <strong>{fmtMoney(schedule.initial_amount)}</strong></span>
        <span>Duration: {schedule.duration_periods ?? 0} mo</span>
        <span>
          Start:{' '}
          {schedule.start_date ? new Date(schedule.start_date).toLocaleDateString() : '-'}
        </span>
      </div>
      <Toolbar align="end">
        <Button size="sm" variant="ghost" onClick={onView}>
          <Eye className="size-4" /> View
        </Button>
        <Button size="sm" variant="ghost" onClick={onEdit}>
          <Pencil className="size-4" /> Edit
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete}>
          <Trash2 className="size-4 text-destructive" /> Void
        </Button>
      </Toolbar>
    </div>
  )
}

function ViewScheduleModal({ id, onClose }: { id: number | null; onClose: () => void }) {
  const detail = useScheduleDetail(id)
  const schedule = detail.data?.schedule
  const entries = detail.data?.entries ?? []
  const brand = useReportBrand()

  useEffect(() => {
    if (id === null) return
    // no-op
  }, [id])

  const onDownloadPdf = () => {
    if (!schedule) return
    downloadSchedulePdf('depreciation', schedule, entries, brand)
  }

  return (
    <Modal
      open={id !== null}
      onOpenChange={(v) => !v && onClose()}
      title={schedule?.description || 'Depreciation schedule'}
      description={
        schedule
          ? `${schedule.category ?? ''} · Cost ${fmtMoney(schedule.initial_amount)} · Method ${schedule.params?.method ?? 'SLN'}`
          : undefined
      }
      className="max-w-3xl"
    >
      {detail.isLoading ? (
        <div className="py-6">
          <LoadingSpinner />
        </div>
      ) : detail.isError ? (
        <Alert variant="destructive">
          <AlertDescription>Failed to load schedule.</AlertDescription>
        </Alert>
      ) : entries.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground">No entries.</p>
      ) : (
        <div className="max-h-[60vh] overflow-auto">
          <DataTable
            sortable={false}
            rows={entries}
            columns={[
              {
                header: () => <span className="block text-center">#</span>,
                accessorKey: 'period_number',
                cell: ({ row }) => (
                  <span className="block text-center text-xs text-muted-foreground">
                    {row.original.period_number}
                  </span>
                ),
              },
              {
                header: 'Period',
                accessorKey: 'entry_date',
                cell: ({ row }) => (
                  <span className="whitespace-nowrap">
                    {new Date(row.original.entry_date).toLocaleDateString()}
                  </span>
                ),
              },
              {
                header: () => <span className="block text-right">Depreciation</span>,
                accessorKey: 'amount',
                cell: ({ row }) => (
                  <span className="block text-right font-mono text-xs">
                    {fmtMoney(row.original.amount)}
                  </span>
                ),
              },
              {
                header: () => <span className="block text-right">Accumulated</span>,
                accessorKey: 'accumulated',
                cell: ({ row }) => (
                  <span className="block text-right font-mono text-xs">
                    {fmtMoney(row.original.accumulated)}
                  </span>
                ),
              },
              {
                header: () => <span className="block text-right">Book value</span>,
                accessorKey: 'balance',
                cell: ({ row }) => (
                  <span className="block text-right font-mono text-xs">
                    {fmtMoney(row.original.balance)}
                  </span>
                ),
              },
              {
                header: 'Posted',
                accessorKey: 'posted',
                cell: ({ row }) => (
                  <Badge variant={row.original.posted ? 'success' : 'secondary'}>
                    {row.original.posted ? 'Yes' : 'No'}
                  </Badge>
                ),
              },
            ]}
          />
        </div>
      )}
      <div className={cn('mt-4 flex flex-wrap justify-end gap-2')}>
        <Button
          variant="secondary"
          onClick={onDownloadPdf}
          disabled={!schedule || entries.length === 0}
        >
          <Download className="size-4" />
          Download PDF
        </Button>
        <Button onClick={onClose}>Close</Button>
      </div>
    </Modal>
  )
}
